import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, CalendarCheck2, ArrowRight, Facebook, Instagram, Linkedin } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import { business } from "@/data/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  organisation: "",
  email: "",
  phone: "",
  enquiry_type: "",
  message: "",
  consent: false,
};

const Contact = () => {
  if (typeof document !== "undefined") document.title = "Contact — School Smiles Australia";
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim()) e.phone = "Required";
    if (!form.enquiry_type) e.enquiry_type = "Please select";
    if (!form.message.trim()) e.message = "Please add a message";
    if (!form.consent) e.consent = "Consent is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return toast.error("Please check the highlighted fields.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries/contact`, form);
      toast.success("Thanks! Your message has been received.");
      setForm(initialState);
    } catch (_) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="container-page pt-16 md:pt-24 pb-8" data-testid="contact-hero">
        <div className="max-w-3xl">
          <span className="eyebrow">Contact</span>
          <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14324A]">
            We'd love to hear from you
          </h1>
          <p className="mt-5 text-[#4B6B86] text-lg leading-relaxed">
            Whether you're a school looking to book a visit or a parent with a question, our team is here to help.
          </p>
        </div>
      </section>

      <section className="container-page py-10 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="contact-info">
        <InfoCard icon={Phone} title="Phone" body={business.phone} href={`tel:${business.phone.replace(/\s+/g, "")}`} testId="contact-info-phone"/>
        <InfoCard icon={Mail} title="Email" body={business.email} href={`mailto:${business.email}`} testId="contact-info-email"/>
        <InfoCard icon={MapPin} title="Service area" body={business.serviceArea} testId="contact-info-area"/>
      </section>

      <section className="container-page py-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="contact-hours-social">
        <div className="card-soft p-8">
          <div className="flex items-center gap-2 text-[#1677C8] font-semibold"><Clock size={18} strokeWidth={2.5}/> Business hours</div>
          <ul className="mt-4 space-y-2 text-sm text-[#4B6B86]">
            {business.hours.map((h) => (
              <li key={h.day} className="flex justify-between"><span className="font-medium text-[#14324A]">{h.day}</span><span>{h.time}</span></li>
            ))}
          </ul>
        </div>
        <div className="card-soft p-8">
          <div className="font-semibold text-[#14324A]">Follow us</div>
          <div className="flex items-center gap-3 mt-4">
            <a href={business.socials.facebook} className="w-11 h-11 rounded-full bg-[#F4F9FF] text-[#1677C8] hover:bg-[#1677C8] hover:text-white transition-colors inline-flex items-center justify-center" aria-label="Facebook" data-testid="contact-social-fb"><Facebook size={18}/></a>
            <a href={business.socials.instagram} className="w-11 h-11 rounded-full bg-[#F4F9FF] text-[#1677C8] hover:bg-[#1677C8] hover:text-white transition-colors inline-flex items-center justify-center" aria-label="Instagram" data-testid="contact-social-ig"><Instagram size={18}/></a>
            <a href={business.socials.linkedin} className="w-11 h-11 rounded-full bg-[#F4F9FF] text-[#1677C8] hover:bg-[#1677C8] hover:text-white transition-colors inline-flex items-center justify-center" aria-label="LinkedIn" data-testid="contact-social-in"><Linkedin size={18}/></a>
          </div>
          <Link to="/for-schools#enquiry" className="btn-accent mt-6 w-fit" data-testid="contact-book-visit-btn">
            <CalendarCheck2 size={18} strokeWidth={2.5}/>
            Book a School Visit
          </Link>
        </div>
      </section>

      <section className="container-page py-12" data-testid="contact-form-section">
        <div className="card-soft p-8 md:p-12">
          <SectionHeading eyebrow="Send a message" title="Contact form"/>
          <form className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5" onSubmit={onSubmit} noValidate data-testid="contact-form">
            <FormField label="Name" error={errors.name}>
              <Input value={form.name} onChange={(e)=>setField("name", e.target.value)} data-testid="input-c-name"/>
            </FormField>
            <FormField label="Organisation or school">
              <Input value={form.organisation} onChange={(e)=>setField("organisation", e.target.value)} data-testid="input-c-org"/>
            </FormField>
            <FormField label="Email" error={errors.email}>
              <Input type="email" value={form.email} onChange={(e)=>setField("email", e.target.value)} data-testid="input-c-email"/>
            </FormField>
            <FormField label="Phone" error={errors.phone}>
              <Input value={form.phone} onChange={(e)=>setField("phone", e.target.value)} data-testid="input-c-phone"/>
            </FormField>
            <FormField label="Enquiry type" error={errors.enquiry_type}>
              <Select value={form.enquiry_type} onValueChange={(v)=>setField("enquiry_type", v)}>
                <SelectTrigger data-testid="select-c-type"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="school-booking">School booking</SelectItem>
                  <SelectItem value="parent-question">Parent question</SelectItem>
                  <SelectItem value="media">Media enquiry</SelectItem>
                  <SelectItem value="careers">Careers</SelectItem>
                  <SelectItem value="general">General enquiry</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <div className="md:col-span-2">
              <Label className="text-sm font-medium text-[#14324A]">Message</Label>
              <Textarea rows={5} value={form.message} onChange={(e)=>setField("message", e.target.value)} data-testid="input-c-message" className="mt-2"/>
              {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
            </div>
            <div className="md:col-span-2 flex items-start gap-3">
              <Checkbox id="c-consent" checked={form.consent} onCheckedChange={(v)=>setField("consent", !!v)} data-testid="checkbox-c-consent"/>
              <label htmlFor="c-consent" className="text-sm text-[#4B6B86] leading-relaxed">
                I consent to School Smiles Australia using this information to respond to me.
              </label>
            </div>
            {errors.consent && <p className="md:col-span-2 text-sm text-red-600 -mt-2">{errors.consent}</p>}
            <div className="md:col-span-2">
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="submit-contact-form">
                {submitting ? "Sending..." : "Send message"}
                <ArrowRight size={18} strokeWidth={2.5}/>
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="container-page py-6" data-testid="contact-map">
        <ImagePlaceholder alt="Google Maps location placeholder — service area covered Australia-wide" ratio="aspect-[16/6]" label="Google Maps placeholder"/>
      </section>
    </>
  );
};

const InfoCard = ({ icon: Icon, title, body, href, testId }) => (
  <div className="card-soft p-8" data-testid={testId}>
    <div className="w-12 h-12 rounded-xl bg-[#F4F9FF] text-[#1677C8] inline-flex items-center justify-center"><Icon size={22} strokeWidth={2.5}/></div>
    <h3 className="mt-4 font-display font-semibold text-lg text-[#14324A]">{title}</h3>
    {href ? (
      <a href={href} className="mt-2 inline-block text-sm text-[#1677C8] hover:text-[#14324A] font-medium leading-relaxed break-all transition-colors">{body}</a>
    ) : (
      <p className="mt-2 text-sm text-[#4B6B86] leading-relaxed">{body}</p>
    )}
  </div>
);

const FormField = ({ label, error, children }) => (
  <div>
    <Label className="text-sm font-medium text-[#14324A]">{label}</Label>
    <div className="mt-2">{children}</div>
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

export default Contact;
